import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Group,
  Stack,
  Alert,
  useMantineColorScheme,
  Box,
  SimpleGrid,
  Card,
} from '@mantine/core';
import CountUpNumber from '../components/animations/CountUpNumber';
import PageTransition from '../components/animations/PageTransition';
import PageSkeleton from '../components/ui/PageSkeleton';
import AdminPageHeader from '../components/AdminPageHeader';
import {
  IconUsers,
  IconCreditCard,
  IconDatabase,
  IconStethoscope,
  IconAlertCircle,
  IconCash,
  IconChevronRight,
} from '@tabler/icons-react';
import { authService } from '../services/authService';
import { AdminDashboardData, UsuarioInfo } from '../types/auth';

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [, setUser] = useState<UsuarioInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (!authService.isAuthenticated()) {
        setError('You are not authenticated');
        return;
      }

      const user = authService.getCurrentUserFromStorage();
      if (user) {
        setUser(user);
      }

      // Obtener datos reales del backend
      const data = await authService.getAdminDashboard();
      setDashboardData(data);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError('Error loading dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Paleta sobria unificada con el perfil de usuario (negro/beige, sin blur ni
  // sombras, sin arcoíris de colores).
  const cardStyle: React.CSSProperties = {
    backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26';
  const mutedColor = colorScheme === 'dark' ? '#94a3b8' : '#8c8478';

  if (loading) {
    return <PageSkeleton variant="grid" count={5} />;
  }

  if (error) {
    return (
      <Box style={{ padding: '32px' }}>
        <Alert icon={<IconAlertCircle size={20} />} color="red" variant="light" radius="md" title="Error">
          {error}
        </Alert>
      </Box>
    );
  }

  // Tarjetas de estadísticas principales (datos reales del backend). El nombre
  // coincide con la pestaña de admin a la que dirige cada tarjeta al hacer clic.
  const statsCards = [
    { icon: IconUsers, value: dashboardData?.stats?.usuariosActivos || 0, label: 'Usuarios', desc: 'Estudiantes activos en la plataforma', duration: 2500, prefix: '', color: textColor, to: '/admin/usuarios' },
    { icon: IconCash, value: dashboardData?.stats?.ingresosMensuales || 0, label: 'Finanzas', desc: 'Ingresos del mes en curso', duration: 3000, prefix: '$', color: '#2f9e44', to: '/admin/finanzas' },
    { icon: IconDatabase, value: dashboardData?.stats?.preguntasTotales || 0, label: 'Reactivos', desc: 'Preguntas en el banco', duration: 3000, prefix: '', color: textColor, to: '/admin/question-database' },
    { icon: IconCreditCard, value: dashboardData?.stats?.suscripcionesActivas || 0, label: 'Suscripciones', desc: 'Suscripciones activas vigentes', duration: 2000, prefix: '', color: textColor, to: '/admin/subscriptions' },
    { icon: IconStethoscope, value: dashboardData?.stats?.examenesCreados || 0, label: 'Casos Clínicos', desc: 'Casos clínicos publicados', duration: 2500, prefix: '', color: textColor, to: '/admin/clinical-cases' },
  ];

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
        <Stack gap="lg">
          <AdminPageHeader
            title="Dashboard de Administrador"
            subtitle="Vista general del sistema y estadísticas principales"
          />

          {/* Tarjetas principales */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
            {statsCards.map((s) => (
              <Card
                key={s.label}
                withBorder
                radius="lg"
                p="lg"
                onClick={() => navigate(s.to)}
                style={{ ...cardStyle, cursor: 'pointer', transition: 'transform 0.15s ease, border-color 0.15s ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = colorScheme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(45,42,38,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.borderColor = colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)';
                }}
              >
                <Stack gap="sm" align="stretch">
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                      <s.icon size={20} color={s.color} style={{ flexShrink: 0 }} />
                      <Text fw={600} size="sm" ta="left" style={{ color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                        {s.label}
                      </Text>
                    </Group>
                    <Group gap={2} align="center" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text size="xs" fw={600} style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                        Ver más
                      </Text>
                      <IconChevronRight size={14} color={mutedColor} />
                    </Group>
                  </Group>

                  <CountUpNumber
                    value={s.value}
                    duration={s.duration}
                    prefix={s.prefix}
                    style={{
                      color: textColor,
                      fontSize: '1.6rem',
                      fontWeight: 700,
                      fontFamily: 'Space Grotesk, Inter, sans-serif',
                      textAlign: 'left',
                    }}
                  />

                  <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                    {s.desc}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Box>
    </PageTransition>
  );
};

export default AdminDashboard;
